import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import {
  LOCK_REQUEST_EVENT,
  PIN_CHANGE_EVENT,
  PIN_STORAGE_KEY,
  hasPin,
  shouldAutoLock,
} from './pinLock';

export interface AppLock {
  locked: boolean;
  unlock: () => void;
  lock: () => void;
}

/**
 * P1-S1 — decides when the PIN screen is up.
 *
 * Locked on first load whenever a PIN exists, again when the page comes back
 * after being hidden longer than the auto-lock interval (a timestamp compare,
 * because timers are throttled in background tabs), and on demand through
 * `requestAppLock()` (panic exit). Removing the PIN unlocks.
 */
export function useAppLock(): AppLock {
  const [locked, setLocked] = useState<boolean>(() => hasPin());
  const hiddenAtRef = useRef<number | null>(null);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
        return;
      }
      const hiddenAt = hiddenAtRef.current;
      hiddenAtRef.current = null;
      if (
        hiddenAt !== null &&
        hasPin() &&
        shouldAutoLock(hiddenAt, Date.now())
      ) {
        setLocked(true);
      }
    };
    const onPinChange = () => {
      if (!hasPin()) setLocked(false);
    };
    const onLockRequest = () => {
      if (hasPin()) setLocked(true);
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === null || event.key === PIN_STORAGE_KEY) onPinChange();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener(PIN_CHANGE_EVENT, onPinChange);
    window.addEventListener(LOCK_REQUEST_EVENT, onLockRequest);
    window.addEventListener('storage', onStorage);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener(PIN_CHANGE_EVENT, onPinChange);
      window.removeEventListener(LOCK_REQUEST_EVENT, onLockRequest);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const unlock = useCallback(() => setLocked(false), []);
  const lock = useCallback(() => {
    if (hasPin()) setLocked(true);
  }, []);

  return { locked, unlock, lock };
}

function subscribeToPin(onChange: () => void): () => void {
  const onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === PIN_STORAGE_KEY) onChange();
  };
  window.addEventListener(PIN_CHANGE_EVENT, onChange);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(PIN_CHANGE_EVENT, onChange);
    window.removeEventListener('storage', onStorage);
  };
}

/** Reactive "is a PIN set?" for the settings screen. */
export function useHasPin(): boolean {
  return useSyncExternalStore(subscribeToPin, hasPin, () => false);
}
